import React from 'react';
import {
    Box,
    TextField,
    Typography,
    Divider,
    Grid,
    FormControlLabel,
    Switch,
    Alert,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import { OnboardingData } from '../CompanyOnboardingWizard';

interface Props {
    data: OnboardingData;
    updateData: (updates: Partial<OnboardingData>) => void;
    errors: Record<string, string>;
}

export const CompanyStep: React.FC<Props> = ({ data, updateData, errors }) => {
    const translate = useTranslate();

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {translate('companyOnboarding.steps.companyTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {translate('companyOnboarding.steps.companyDescription')}
            </Typography>

            {/* Issue #269: Demo-Toggle direkt im ersten Step — schaltet die
                Step-Liste um (Demo-Daten-Step rein, License-Step raus). */}
            <Box sx={{ mb: 3, p: 2, bgcolor: data.isDemoTenant ? 'warning.lighter' : 'grey.50', borderRadius: 1 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={data.isDemoTenant}
                            onChange={(e) => updateData({ isDemoTenant: e.target.checked })}
                            color="warning"
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="subtitle2">
                                {translate('companyOnboarding.fields.isDemoTenant')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {translate('companyOnboarding.fields.isDemoTenantHelp')}
                            </Typography>
                        </Box>
                    }
                />
                {data.isDemoTenant && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        {translate('companyOnboarding.warnings.demoMode')}
                    </Alert>
                )}
            </Box>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        label={translate('companyOnboarding.fields.companyName')}
                        value={data.companyName}
                        onChange={(e) => updateData({ companyName: e.target.value })}
                        fullWidth
                        required
                        error={!!errors.companyName}
                        helperText={errors.companyName}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        label={translate('companyOnboarding.fields.companyEmail')}
                        type="email"
                        value={data.companyEmail}
                        onChange={(e) => updateData({ companyEmail: e.target.value })}
                        fullWidth
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        label={translate('companyOnboarding.fields.companyPhone')}
                        value={data.companyPhone}
                        onChange={(e) => updateData({ companyPhone: e.target.value })}
                        fullWidth
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        label={translate('companyOnboarding.fields.companyWebsite')}
                        type="url"
                        value={data.companyWebsite}
                        onChange={(e) => updateData({ companyWebsite: e.target.value })}
                        fullWidth
                        placeholder="https://www.firma.de"
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" gutterBottom>
                {translate('companyOnboarding.sections.address')}
            </Typography>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        label={translate('companyOnboarding.fields.street')}
                        value={data.companyStreet}
                        onChange={(e) => updateData({ companyStreet: e.target.value })}
                        fullWidth
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        label={translate('companyOnboarding.fields.zip')}
                        value={data.companyZip}
                        onChange={(e) => updateData({ companyZip: e.target.value })}
                        fullWidth
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                    <TextField
                        label={translate('companyOnboarding.fields.city')}
                        value={data.companyCity}
                        onChange={(e) => updateData({ companyCity: e.target.value })}
                        fullWidth
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        label={translate('companyOnboarding.fields.country')}
                        value={data.companyCountry}
                        onChange={(e) => updateData({ companyCountry: e.target.value })}
                        fullWidth
                    />
                </Grid>
            </Grid>
        </Box>
    );
};
